import React from 'react';
import { Button } from 'react-bootstrap';
import Select from 'react-select';

interface TeamsHeaderProps {
  title: string;
  description: string;
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
  ageGroupFilter: string;
  onAgeGroupFilterChange: (filter: string) => void;
  statusFilter?: string;
  onStatusFilterChange?: (filter: string) => void;
  sortFilter?: string;
  onSortFilterChange?: (filter: string) => void;
  onAddTeam: () => void;
  addButtonText?: string;
  showFilters?: boolean;
  ageCategories?: string[];
}

const TeamsHeader: React.FC<TeamsHeaderProps> = ({
  title,
  description,
  searchTerm,
  onSearchChange,
  ageGroupFilter,
  onAgeGroupFilterChange,
  statusFilter = 'all',
  onStatusFilterChange,
  sortFilter = 'all',
  onSortFilterChange,
  onAddTeam,
  addButtonText = "Add New Team",
  showFilters = true,
  ageCategories = []
}) => {
  // Age group options for react-select - use dynamic data if available, fallback to default
  const ageGroupOptions = [
    { value: 'all', label: 'All' },
    ...(ageCategories.length > 0 
      ? ageCategories.map(category => ({ value: category, label: category }))
      : [
          { value: 'U11', label: 'U11' },
          { value: 'U12', label: 'U12' },
          { value: 'U13', label: 'U13' },
          { value: 'U14', label: 'U14' },
          { value: 'U15', label: 'U15' },
          { value: 'U16', label: 'U16' },
          { value: 'U17', label: 'U17' },
          { value: 'U18', label: 'U18' }
        ]
    )
  ];

  // Status options
  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  // Sort options
  const sortOptions = [
    { value: 'all', label: 'All' },
    { value: 'name', label: 'Name' },
    { value: 'teams', label: 'Teams' },
    { value: 'manager', label: 'Manager' },
    { value: 'location', label: 'Location' }
  ];

  return (
    <div className="teams-header-section mb-4" style={{ position: 'relative', zIndex: 10, padding: '0', background: 'transparent', margin: '0', backgroundColor: 'transparent' }}>
      {/* Single Row: Title/Description on Left, Filters and Add Button on Right */}
      <div className="d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 10, width: '100%', padding: '0', background: 'transparent', backgroundColor: 'transparent' }}>
        {/* Left: Teams Title and Description */}
        <div className="teams-section-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: '0 0 auto', background: 'transparent', backgroundColor: 'transparent' }}>
          <h4 className="section-title mb-2" style={{ fontSize: '20px', fontWeight: '600', color: '#495057', margin: '0 0 8px 0', lineHeight: '1.3' }}>{title}</h4>
          <p className="section-description mb-0" style={{ fontSize: '14px', color: '#6c757d', margin: '0', lineHeight: '1.4' }}>{description}</p>
        </div>
        
        {/* Right: Filters and Add Button */}
        <div className="filter-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end', background: 'transparent', backgroundColor: 'transparent' }}>
          {showFilters && (
            <>
              <div className="inline-filters d-flex align-items-center gap-2">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    className="form-search"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    style={{ width: '200px', paddingRight: '35px' }}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onClick={() => onSearchChange('')}
                      title="Clear search"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
                <Select
                  classNamePrefix="react-select"
                  value={ageGroupOptions.find(option => option.value === ageGroupFilter)}
                  onChange={(selectedOption) => onAgeGroupFilterChange(selectedOption?.value || 'all')}
                  options={ageGroupOptions}
                  placeholder="All"
                  styles={{
                    control: (base) => ({ 
                      ...base, 
                      minHeight: '40px', 
                      width: '120px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: 'none'
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  isSearchable={false}
                />

                {/* Status Filter */}
                {onStatusFilterChange && (
                  <Select
                    classNamePrefix="react-select"
                    value={statusOptions.find(option => option.value === statusFilter)}
                    onChange={(selectedOption) => onStatusFilterChange(selectedOption?.value || 'all')}
                    options={statusOptions}
                    placeholder="All"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '40px', 
                        width: '120px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    isSearchable={false}
                  />
                )}

                {/* Sort Filter */}
                {onSortFilterChange && (
                  <Select
                    classNamePrefix="react-select"
                    value={sortOptions.find(option => option.value === sortFilter)}
                    onChange={(selectedOption) => onSortFilterChange(selectedOption?.value || 'all')}
                    options={sortOptions}
                    placeholder="All"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '40px', 
                        width: '120px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    isSearchable={false}
                  />
                )}
              </div>
            </>
          )}
          <Button 
            variant="secondary" 
            onClick={onAddTeam}
            className="add-team-btn"
            style={{ 
              position: 'relative', 
              zIndex: 15,
              backgroundColor: '#6c757d',
              borderColor: '#6c757d',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '120px',
              justifyContent: 'center'
            }}
          >
            {addButtonText}
            <i className="ri-add-line" style={{ fontSize: '16px' }}></i>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeamsHeader; 